import { agentControllerRefSchema } from "../daemon/agent-controller-ref.js";
import { openMeldRequestContextSchema } from "../daemon/profile-identity.js";
import { projectSlugSchema, repositoryFingerprintSchema } from "./projects.js";
import { centerAgentToolSpeakerSchema } from "./center-agent-speaker.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/agent-activity-sessions.ts
const AGENT_ACTIVITY_SESSION_INVENTORY_PAGE_DEFAULT = 50;
const AGENT_ACTIVITY_SESSION_INVENTORY_PAGE_MAX = 100;
const AGENT_ACTIVITY_SESSION_RECONCILIATION_PAGE_MAX = 100;
const AGENT_ACTIVITY_SESSION_RECONCILIATION_MAX_BYTES = 256 * 1024;
const AGENT_ACTIVITY_SESSION_TITLE_MAX_CHARS = 240;
const LOCAL_AGENT_SESSION_PROVIDERS = [
	"codex",
	"claude-code",
	"cursor",
	"opencode",
	"grok-build"
];
const LOCAL_AGENT_CONTROLLER_REF_BY_PROVIDER = {
	"claude-code": "builtin:claude-code",
	codex: "builtin:codex",
	cursor: "builtin:cursor",
	"grok-build": "builtin:grok-build",
	opencode: "builtin:opencode"
};
const boundedIdentifierSchema = z$1.string().trim().min(1).max(512);
const displayNameSchema = z$1.string().trim().min(1).max(256);
const timestampMsSchema = z$1.number().int().nonnegative();
const agentActivityNativeSessionIdSchema = boundedIdentifierSchema;
const localAgentSessionProviderSchema = z$1.enum(LOCAL_AGENT_SESSION_PROVIDERS);
const agentActivityInventorySessionStatusSchema = z$1.enum([
	"working",
	"idle",
	"ended",
	"error",
	"unknown"
]);
const agentActivityInventorySessionOriginSchema = z$1.enum([
	"openmeld",
	"local",
	"unknown"
]);
const agentActivityInventoryComputerStateSchema = z$1.enum([
	"online",
	"offline",
	"unknown"
]);
const agentActivityInventoryDataStateSchema = z$1.enum(["current", "stale"]);
const agentActivityInventoryBindingStateSchema = z$1.enum([
	"bound",
	"unbound",
	"conflict"
]);
const agentActivityInventoryCoverageStateSchema = z$1.enum([
	"waiting",
	"syncing",
	"current",
	"stale",
	"unsupported",
	"error",
	"disconnected"
]);
const agentActivityInventoryOrganizationSchema = z$1.strictObject({
	id: boundedIdentifierSchema,
	name: displayNameSchema
});
const agentActivityInventoryOwnerSchema = z$1.strictObject({
	name: displayNameSchema,
	userId: boundedIdentifierSchema
});
const agentActivityInventoryProjectSchema = z$1.strictObject({
	id: boundedIdentifierSchema,
	name: displayNameSchema,
	slug: projectSlugSchema
});
const agentActivityInventoryComputerSchema = z$1.strictObject({
	id: boundedIdentifierSchema,
	lastSeenAtMs: timestampMsSchema.nullable(),
	name: displayNameSchema,
	state: agentActivityInventoryComputerStateSchema
});
const agentActivityInventoryLocalAgentSchema = z$1.strictObject({
	agentControllerRef: agentControllerRefSchema,
	name: displayNameSchema,
	provider: localAgentSessionProviderSchema
}).superRefine(refineLocalAgentProviderControllerPair);
const agentActivityInventorySessionSchema = z$1.strictObject({
	boundAgentProfileId: boundedIdentifierSchema.nullable(),
	bindingState: agentActivityInventoryBindingStateSchema,
	canFork: z$1.boolean(),
	canResume: z$1.boolean(),
	computer: agentActivityInventoryComputerSchema,
	dataState: agentActivityInventoryDataStateSchema,
	inventoryUpdatedAtMs: timestampMsSchema,
	lastActiveAtMs: timestampMsSchema,
	localAgent: agentActivityInventoryLocalAgentSchema,
	nativeSessionId: agentActivityNativeSessionIdSchema,
	nativeSessionTitle: z$1.string().trim().min(1).max(240).nullable(),
	organization: agentActivityInventoryOrganizationSchema,
	origin: agentActivityInventorySessionOriginSchema,
	owner: agentActivityInventoryOwnerSchema,
	project: agentActivityInventoryProjectSchema,
	startedAtMs: timestampMsSchema.nullable(),
	status: agentActivityInventorySessionStatusSchema
}).superRefine((value, context) => {
	if (value.bindingState === "bound" !== (value.boundAgentProfileId !== null)) context.addIssue({
		code: "custom",
		message: "A bound Session requires exactly one authoritative Agent Profile.",
		path: ["boundAgentProfileId"]
	});
	if (value.startedAtMs !== null && value.startedAtMs > value.lastActiveAtMs) context.addIssue({
		code: "custom",
		message: "Session start time cannot follow its last activity.",
		path: ["startedAtMs"]
	});
});
const agentActivityInventoryCoverageSchema = z$1.strictObject({
	agentControllerRef: agentControllerRefSchema,
	computerId: boundedIdentifierSchema,
	lastAttemptAtMs: timestampMsSchema.nullable(),
	lastCompleteAtMs: timestampMsSchema.nullable(),
	problemCode: z$1.string().trim().min(1).max(128).nullable(),
	projectId: boundedIdentifierSchema,
	provider: localAgentSessionProviderSchema,
	state: agentActivityInventoryCoverageStateSchema
}).superRefine(refineLocalAgentProviderControllerPair);
const agentActivitySessionInventoryQuerySchema = z$1.strictObject({
	cursor: z$1.string().trim().min(1).max(2048).optional(),
	limit: z$1.coerce.number().int().positive().max(100).default(50),
	memberUserId: boundedIdentifierSchema.optional(),
	nativeSessionId: agentActivityNativeSessionIdSchema.optional(),
	origin: agentActivityInventorySessionOriginSchema.optional(),
	projectId: boundedIdentifierSchema.optional(),
	provider: localAgentSessionProviderSchema.optional(),
	query: z$1.string().trim().min(1).max(256).optional(),
	status: agentActivityInventorySessionStatusSchema.optional()
});
const agentActivitySessionInventoryResponseSchema = z$1.strictObject({
	asOfMs: timestampMsSchema,
	coverage: z$1.array(agentActivityInventoryCoverageSchema),
	nextCursor: z$1.string().trim().min(1).max(2048).nullable(),
	organization: agentActivityInventoryOrganizationSchema,
	sessions: z$1.array(agentActivityInventorySessionSchema).max(100)
});
const agentActivitySessionInventoryRpcInputSchema = z$1.strictObject({
	context: openMeldRequestContextSchema.optional(),
	...agentActivitySessionInventoryQuerySchema.shape
});
const centerAgentListAgentActivitySessionsToolRpcInputSchema = z$1.strictObject({
	context: openMeldRequestContextSchema,
	...agentActivitySessionInventoryQuerySchema.shape,
	speakerMember: centerAgentToolSpeakerSchema
});
const observedLocalAgentSessionSchema = z$1.strictObject({
	canFork: z$1.boolean(),
	canResume: z$1.boolean(),
	lastActiveAtMs: timestampMsSchema,
	nativeSessionId: agentActivityNativeSessionIdSchema,
	nativeSessionTitle: z$1.string().trim().min(1).max(240).nullable(),
	origin: agentActivityInventorySessionOriginSchema,
	startedAtMs: timestampMsSchema.nullable(),
	status: agentActivityInventorySessionStatusSchema
}).superRefine((value, context) => {
	if (value.startedAtMs !== null && value.startedAtMs > value.lastActiveAtMs) context.addIssue({
		code: "custom",
		message: "Session start time cannot follow its last activity.",
		path: ["startedAtMs"]
	});
});
const agentActivitySessionReconciliationResultSchema = z$1.enum([
	"sessions",
	"unsupported",
	"error",
	"disconnected"
]);
const submitAgentActivitySessionReconciliationBodySchema = z$1.strictObject({
	agentControllerRef: agentControllerRefSchema,
	lane: boundedIdentifierSchema,
	localConnectionId: z$1.string().regex(/^sha256:[a-f0-9]{64}$/u),
	observedAtMs: timestampMsSchema,
	openMeldServiceRuntimeId: z$1.strictObject({
		source: z$1.literal("runtime_registry_authority"),
		value: boundedIdentifierSchema
	}),
	pageIndex: z$1.number().int().nonnegative(),
	organizationId: boundedIdentifierSchema,
	projectId: boundedIdentifierSchema,
	provider: localAgentSessionProviderSchema,
	reconciliationId: boundedIdentifierSchema,
	repositoryFingerprint: repositoryFingerprintSchema.nullable(),
	result: agentActivitySessionReconciliationResultSchema,
	sessions: z$1.array(observedLocalAgentSessionSchema).max(100),
	terminal: z$1.boolean(),
	problemCode: z$1.string().trim().min(1).max(128).optional()
}).superRefine((value, context) => {
	refineLocalAgentProviderControllerPair(value, context);
	const nativeSessionIds = /* @__PURE__ */ new Set();
	for (const [index, session] of value.sessions.entries()) {
		if (nativeSessionIds.has(session.nativeSessionId)) context.addIssue({
			code: "custom",
			message: "A reconciliation page cannot contain the same native Session ID twice.",
			path: [
				"sessions",
				index,
				"nativeSessionId"
			]
		});
		nativeSessionIds.add(session.nativeSessionId);
	}
	if (value.result === "sessions") {
		if (value.problemCode !== void 0) context.addIssue({
			code: "custom",
			message: "Successful reconciliation pages cannot include a problem.",
			path: ["problemCode"]
		});
		return;
	}
	if (value.sessions.length > 0 || !value.terminal || value.pageIndex !== 0) context.addIssue({
		code: "custom",
		message: "Non-session reconciliation outcomes must be one empty terminal page.",
		path: ["sessions"]
	});
	if ((value.result === "unsupported" || value.result === "error") !== (value.problemCode !== void 0)) context.addIssue({
		code: "custom",
		message: "Unsupported and error outcomes require exactly one stable problem code.",
		path: ["problemCode"]
	});
});
function refineLocalAgentProviderControllerPair(value, context) {
	if (value.agentControllerRef !== LOCAL_AGENT_CONTROLLER_REF_BY_PROVIDER[value.provider]) context.addIssue({
		code: "custom",
		message: "Local Agent provider and Agent Controller reference must identify the same provider.",
		path: ["agentControllerRef"]
	});
}
const submitAgentActivitySessionReconciliationResponseSchema = z$1.strictObject({
	acceptedPageIndex: z$1.number().int().nonnegative(),
	publishedSessionCount: z$1.number().int().nonnegative().nullable(),
	reconciliationId: boundedIdentifierSchema,
	status: z$1.enum([
		"accepted",
		"published",
		"reused"
	])
});
const submitAgentActivitySessionReconciliationRpcInputSchema = submitAgentActivitySessionReconciliationBodySchema.safeExtend({
	context: openMeldRequestContextSchema.optional(),
	deviceId: boundedIdentifierSchema
});
//#endregion
export { AGENT_ACTIVITY_SESSION_INVENTORY_PAGE_DEFAULT, AGENT_ACTIVITY_SESSION_INVENTORY_PAGE_MAX, AGENT_ACTIVITY_SESSION_RECONCILIATION_MAX_BYTES, AGENT_ACTIVITY_SESSION_RECONCILIATION_PAGE_MAX, AGENT_ACTIVITY_SESSION_TITLE_MAX_CHARS, LOCAL_AGENT_SESSION_PROVIDERS, agentActivityInventoryBindingStateSchema, agentActivityInventoryComputerSchema, agentActivityInventoryComputerStateSchema, agentActivityInventoryCoverageSchema, agentActivityInventoryCoverageStateSchema, agentActivityInventoryDataStateSchema, agentActivityInventoryLocalAgentSchema, agentActivityInventoryOrganizationSchema, agentActivityInventoryOwnerSchema, agentActivityInventoryProjectSchema, agentActivityInventorySessionOriginSchema, agentActivityInventorySessionSchema, agentActivityInventorySessionStatusSchema, agentActivityNativeSessionIdSchema, agentActivitySessionInventoryQuerySchema, agentActivitySessionInventoryResponseSchema, agentActivitySessionInventoryRpcInputSchema, agentActivitySessionReconciliationResultSchema, centerAgentListAgentActivitySessionsToolRpcInputSchema, localAgentSessionProviderSchema, observedLocalAgentSessionSchema, submitAgentActivitySessionReconciliationBodySchema, submitAgentActivitySessionReconciliationResponseSchema, submitAgentActivitySessionReconciliationRpcInputSchema };

//# sourceMappingURL=agent-activity-sessions.js.map